import type {
  CatalogProviderListingSummary,
  CatalogProviderSummary,
  CatalogProviderWaitlistSummary
} from './catalog.types';
import { isListingAvailable } from '@paction-2.0/util/listings/availability';

import { formatCurrency, formatDateLabel, isNumber } from '@/lib/format';

const formatAvailability = (listing: CatalogProviderListingSummary) => {
  if (isListingAvailable(listing.isAvailableNow, listing.availableDate)) {
    return 'Available now';
  }
  const formatted = formatDateLabel(listing.availableDate ?? undefined);
  return formatted ? `Ready ${formatted}` : undefined;
};
const formatPriceRange = (
  min?: number | null,
  max?: number | null
): string | undefined => {
  if (isNumber(min) && isNumber(max)) {
    if (min === max) return formatCurrency(min);
    return `${formatCurrency(min)} – ${formatCurrency(max)}`;
  }
  if (isNumber(min)) return formatCurrency(min);
  if (isNumber(max)) return formatCurrency(max);
  return undefined;
};
const formatLocation = (city?: string | null, province?: string | null) => {
  if (city && province) return `${city}, ${province}`;
  if (province) return province;
  if (city) return city;
  return undefined;
};
const mapListingSnippet = (listing: CatalogProviderListingSummary) => {
  const title = listing.title ?? listing.breed ?? 'Puppy listing';
  const priceLabel = formatCurrency(listing.price);
  const availabilityLabel = formatAvailability(listing);
  return {
    id: listing.id,
    title,
    breed: listing.breed ?? undefined,
    priceLabel,
    availabilityLabel
  };
};
const mapWaitlistSnippet = (waitlist: CatalogProviderWaitlistSummary) => {
  const title = waitlist.title ?? waitlist.breed ?? 'Waitlist';
  const priceLabel = formatPriceRange(waitlist.priceMin, waitlist.priceMax);
  return {
    id: waitlist.id,
    title,
    breed: waitlist.breed ?? undefined,
    priceLabel
  };
};

export type ProviderHighlight = {
  id: number;
  slug: string;
  provinceSlug?: string | null;
  name: string;
  imageUrl?: string | null;
  location?: string;
  averageRating?: number | null;
  numberOfRatings?: number | null;
  isVerified: boolean;
  listings: ReturnType<typeof mapListingSnippet>[];
  waitlists: ReturnType<typeof mapWaitlistSnippet>[];
};

export const toProviderHighlights = (
  providers: CatalogProviderSummary[],
  limit = 6
): ProviderHighlight[] => {
  const safeLimit = Math.max(0, limit);
  return providers.slice(0, safeLimit).map((provider) => ({
    id: provider.id,
    slug: provider.slug,
    provinceSlug: provider.provinceSlug,
    name: provider.name,
    imageUrl: provider.imageUrl,
    location: formatLocation(provider.city, provider.province),
    averageRating: provider.averageRating,
    numberOfRatings: provider.numberOfRatings,
    isVerified: provider.isIdVerified,
    listings: provider.listings.map(mapListingSnippet),
    waitlists: provider.waitlists.map(mapWaitlistSnippet)
  }));
};
export { formatLocation };
